import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  Event,
  LocationOn,
  Person,
  MoreVert,
  Visibility,
  Edit,
} from '@mui/icons-material';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  addDays,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { getCalendarUserColor } from '../../utils/calendarUserColors';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  eventType: 'client_meeting' | 'court_hearing' | 'deposition' | 'mediation' | 'consultation' | 'filing_deadline' | 'document_review' | 'billing_review' | 'staff_meeting' | 'training' | 'other';
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reminderEnabled: boolean;
  reminderTime: number;
  reminderSent: boolean;
  notes: string | null;
  attendees: any[];
  recurrence: any;
  case: { id: string; caseNumber: string; title: string; } | null;
  client: { id: string; name: string; } | null;
  task: { id: string; title: string; status: string; } | null;
  creator: { id: string; name: string; email: string; } | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface WeekViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
  onEventEdit: (event: CalendarEvent) => void;
  onEventView: (event: CalendarEvent) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  events,
  currentDate,
  onEventClick,
  onEventEdit,
  onEventView,
}) => {
  const theme = useTheme();
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getEventColor = (event: CalendarEvent) =>
    getCalendarUserColor(event.creator, theme.palette.grey[500]);

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: theme.palette.success.main,
      medium: theme.palette.warning.main,
      high: theme.palette.error.main,
      urgent: theme.palette.error.dark,
    };
    return colors[priority] || theme.palette.grey[500];
  };

  const getEventsForDay = (date: Date) => {
    const dayEvents = events.filter(event => {
      const eventStart = parseISO(event.startDate);
      const eventEnd = parseISO(event.endDate);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      const isInDay = isWithinInterval(eventStart, { start: dayStart, end: dayEnd }) ||
             isWithinInterval(eventEnd, { start: dayStart, end: dayEnd }) ||
             (eventStart <= dayStart && eventEnd >= dayEnd);
      
      // Debug logging for target event
      if (event.id === 'b49cbecc-9833-4cdd-bf90-1457d02df722') {
        console.log('WeekView - Checking target event for day:', format(date, 'yyyy-MM-dd'));
        console.log('Event start:', eventStart);
        console.log('Event end:', eventEnd);
        console.log('Day start:', dayStart);
        console.log('Day end:', dayEnd);
        console.log('Is in day:', isInDay);
      }
      
      return isInDay;
    });
    
    if (dayEvents.length > 0) {
      console.log(`WeekView - Found ${dayEvents.length} events for ${format(date, 'yyyy-MM-dd')}:`, dayEvents.map(e => e.title));
    }
    
    return dayEvents;
  };

  const formatTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'HH:mm');
    } catch (error) {
      return 'Invalid time';
    }
  };

  const handleEventClick = (event: CalendarEvent) => {
    console.log('WeekView - Event clicked:', event.title);
    // Always open the event details dialog instead of navigating away
    onEventClick(event);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 300px)', overflow: 'auto' }}>
      <Grid container spacing={1}>
        {/* Time column */}
        <Grid item xs={1}>
          <Box sx={{ height: 60 }} /> {/* Header spacer */}
          {Array.from({ length: 24 }, (_, hour) => (
            <Box
              key={hour}
              sx={{
                height: 60,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
              }}
            >
              {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
            </Box>
          ))}
        </Grid>

        {/* Days columns */}
        {weekDays.map((day, dayIndex) => (
          <Grid item xs key={dayIndex}>
            <Box>
              {/* Day header */}
              <Paper
                sx={{
                  p: 1,
                  textAlign: 'center',
                  backgroundColor: isSameDay(day, new Date()) 
                    ? theme.palette.primary.main 
                    : theme.palette.background.paper,
                  color: isSameDay(day, new Date()) 
                    ? theme.palette.primary.contrastText 
                    : theme.palette.text.primary,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {format(day, 'EEE')}
                </Typography>
                <Typography variant="h6">
                  {format(day, 'd')}
                </Typography>
              </Paper>

              {/* Day content */}
              <Box sx={{ position: 'relative', height: 'calc(100vh - 360px)' }}>
                {/* Time slots */}
                {Array.from({ length: 24 }, (_, hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: 60,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'relative',
                    }}
                  />
                ))}

                {/* Events */}
                {getEventsForDay(day).map((event) => {
                  const eventStart = parseISO(event.startDate);
                  const eventEnd = parseISO(event.endDate);
                  const startHour = eventStart.getHours();
                  const startMinute = eventStart.getMinutes();
                  const endHour = eventEnd.getHours();
                  const endMinute = eventEnd.getMinutes();
                  
                  const top = startHour * 60 + startMinute;
                  const height = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
                  
                  return (
                    <Card
                      key={event.id}
                      sx={{
                        position: 'absolute',
                        top: top,
                        left: 4,
                        right: 4,
                        height: Math.max(height, 30),
                        backgroundColor: getEventColor(event),
                        color: 'white',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          transform: 'scale(1.02)',
                          zIndex: 10,
                        },
                        transition: 'all 0.2s ease-in-out',
                        zIndex: hoveredEvent === event.id ? 10 : 1,
                      }}
                      onMouseEnter={() => setHoveredEvent(event.id)}
                      onMouseLeave={() => setHoveredEvent(null)}
                      onClick={() => handleEventClick(event)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 'bold',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {event.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                opacity: 0.8,
                                fontSize: '0.7rem',
                              }}
                            >
                              {formatTime(event.startDate)} - {formatTime(event.endDate)}
                            </Typography>
                            {event.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <LocationOn sx={{ fontSize: '0.7rem' }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  {event.location}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          {hoveredEvent === event.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEventView(event);
                                  }}
                                  sx={{ color: 'white' }}
                                >
                                  <Visibility sx={{ fontSize: '0.8rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEventEdit(event);
                                  }}
                                  sx={{ color: 'white' }}
                                >
                                  <Edit sx={{ fontSize: '0.8rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {/* Case indicator */}
                        {event.case && (
                          <Chip
                            label={`Case: ${event.case.caseNumber}`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              fontSize: '0.6rem',
                              height: 16,
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WeekView; 